import React, { useState } from 'react';
import { Modal, Button, Typography, Input, Form } from '@crisellevaldez/speculo-ui';
import { AlertCircle } from 'lucide-react';

interface InputConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  confirmationText: string;
}

export const InputConfirmation: React.FC<InputConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  confirmationText: requiredText,
}) => {
  const [inputText, setInputText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
    if (error) setError(null);
  };

  const handleClose = () => {
    setInputText('');
    setError(null);
    onClose();
  };

  const handleConfirm = () => {
    if (inputText !== requiredText) {
      setError("Text doesn't match. Please try again.");
      return;
    }

    onConfirm();
    setInputText('');
    setError(null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      <Modal.Header>
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{title}</h3>
        </div>
      </Modal.Header>
      <Modal.Body>
        <Typography variant="p" className="text-gray-600 dark:text-gray-300">
          {message}
        </Typography>

        <div className="mt-4">
          <Form.Control required>
            <Form.Label>Type "{requiredText}" to confirm</Form.Label>
            <Input
              value={inputText}
              onChange={handleInputChange}
              onPaste={(e) => e.preventDefault()} // Prevent paste
              onCopy={(e) => e.preventDefault()} // Prevent copy
              placeholder={`Type ${requiredText} here`}
              error={error || undefined}
            />
          </Form.Control>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} size="sm" disabled={isLoading}>
            {cancelLabel}
          </Button>
          <Button
            onClick={handleConfirm}
            size="sm"
            variant="secondary"
            className="bg-red-700 hover:bg-red-600 text-white"
            isLoading={isLoading}
            disabled={isLoading || inputText !== requiredText}
          >
            {confirmLabel}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InputConfirmation;
